
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import axios from 'axios'

import { ApplicationModule, AuthModule } from '@/store'
import { ADF, URLS } from '@/factories/index'
import AfdHelper, { postcodeRegex } from '@/helpers/afd-helper'

import AddressLookupResult from '@/types/address/addressLookupResult'
import AddressDto from '@/types/dto/addressDto'
import AddressLookupResultItem from '@/types/address/addressLookupResultItem'
import ApiResponse from '@/types/apiResponse'
import { IAppForm } from '../forms/IAppForm'
import CFInput from '../inputs/CFInput'

	@Component({
	  name: 'AddressLookup'
	})
export default class AddressLookup extends Vue implements IAppForm {
  // #region Props

		@Prop({ default: 0 }) index!: number;
		@Prop({ default: false }) manual!: boolean;
		@Prop({ default: null }) preAddress!: AddressDto | null;

		// #endregion

		// #region Data

		hideAddressLookup = false;
		isAddressHidden = true;
		showAddressError = false;

		postcode = '';
		previousPostcode = '';

		address: AddressLookupResultItem = {} as AddressLookupResultItem;
		addresses: AddressLookupResultItem[] = [];
		manualAddress: AddressDto = {} as AddressDto;

		// #endregion

		// #region Methods

		mounted () {
		  if (this.preAddress) {
		    this.manualAddress = this.preAddress
		    if (this.preAddress.postcode !== undefined && this.preAddress.postcode !== '') {
		      this.setManual()
		    }
		  }
		}

		setManual () {
		  this.$emit('toggle-manual')
		}

		fastPostcodeLookupURL (address: string) {
		  const uniqueId = ADF.AfdListQueryParams.uniqueId
		  const data = ADF.AfdListQueryParams.data
		  const task = ADF.AfdListQueryParams.task
		  const fields = ADF.AfdListQueryParams.fields
		  const maxQuantity = ADF.AfdListQueryParams.maxQuantity
		  const format = ADF.AfdListQueryParams.format
		  const requestURL = AuthModule.isProduction ? URLS.production.afdPostcodeLookup : URLS.replica.afdPostcodeLookup

		  return encodeURI(requestURL + '?uniqueid=' + uniqueId + '&Data=' + data + '&Task=' + task + '&Fields=' + fields + '&maxquantity=' + maxQuantity + '&Format=' + format + '&Lookup=' + address + '&Nearest=FastFind')
		}

		getAddressByKeyAfdURL (key: string) {
		  const data = ADF.AfdSingleLookUp.data
		  const task = ADF.AfdSingleLookUp.task
		  const fields = ADF.AfdSingleLookUp.fields
		  const format = ADF.AfdSingleLookUp.format

		  const requestURL = AuthModule.isProduction ? URLS.production.afdPostcodeLookup : URLS.replica.afdPostcodeLookup

		  return encodeURI(requestURL + '?Data=' + data + '&Task=' + task + '&Fields=' + fields + '&Format=' + format + '&Key=' + key)
		}

		async postcodeLookup () {
		  // reset addresses
		  this.addresses = []
		  this.isAddressHidden = false

		  try {
		    // Make fast lookup call
		    const response = await axios.get(this.fastPostcodeLookupURL(this.address.Postcode!), { timeout: 30000 })

		    if (response === null || response.status === null || response.status !== 200) {
		      // TODO Handle no result
		      return
		    }

		    // Cast as api response
		    const apiResponse = (response.data as ApiResponse)

		    if (apiResponse.Result === '1') {
		      // Cast get result as Address item
		      const addrs = apiResponse.Item as unknown as AddressLookupResultItem[]

		      // Format each address town and add to list of addresses
		      addrs.forEach((el: AddressLookupResultItem) => {
		        this.address.Postcode = this.address.Postcode!.toUpperCase()

		        el.List = AfdHelper.formatAddressTown(el.List, el.Town)
		        this.addresses.push(el)
		      })
		    }
		  } catch (error) {
		    console.log(error)
		  } finally {
		    //
		  }
		}

		validate () {
		  if (this.manual) {
		    const flatNo = (this.$refs.flatNo as unknown as CFInput).validate()
		    const houseNo = (this.$refs.houseNo as unknown as CFInput).validate()
		    const buildingName = (this.$refs.buildingName as unknown as CFInput).validate()
		    const street = (this.$refs.street as unknown as CFInput).validate()
		    // const locality = (this.$refs.locality as unknown as CFInput).validate()
		    const town = (this.$refs.town as unknown as CFInput).validate()
		    const postcode = (this.$refs.postcode as unknown as CFInput).validate()

		    if (flatNo && houseNo && buildingName && street && town && postcode) {
		      return true
		    }
		    return false
		  } else {
		    if (this.address.Postcode && this.address.Postcode !== '') {
		      this.showAddressError = false
		      return true
		    }
		    this.showAddressError = true
		    return false
		  }
		}

		submit () {
		  return null
		}

		async addressLookup (key: string) {
		  // reset addresses
		  this.address = {} as AddressLookupResultItem
		  this.isAddressHidden = false

		  try {
		    // Make fast lookup call
		    const response = await axios.get(this.getAddressByKeyAfdURL(key), { timeout: 30000 })

		    if (response === null || response.status === null || response.status !== 200) {
		      // TODO Handle no result
		      return
		    }

		    // Cast as api response
		    const apiResponse = (response.data as AddressLookupResult)

		    if (apiResponse.Result === '1') {
		      // Cast get result as Address item
		      const addrs = apiResponse.Item as AddressLookupResultItem[]

		      if (addrs.length === 0) {
		        this.manual = true
		        return
		      }

		      this.showAddressError = false

		      this.address = addrs[0]

		      this.manualAddress = {
		        yearsAtAddress: 0,
		        monthsAtAddress: 0,
		        addressLevel: ApplicationModule.addresses.length,
		        addressId: 0,
		        residentialStatusLookup: {},
		        flatNumber: AfdHelper.extractFlatNumber(addrs[0].Property),
		        buildingName: addrs[0].Property !== '' ? addrs[0].Property : addrs[0].Organisation,
		        subBuilding: addrs[0].Property,
		        buildingNumber: AfdHelper.extractHouseNumber(addrs[0].Street),
		        street: AfdHelper.mapStreet(addrs[0].Street),
		        locality: addrs[0].Locality,
		        town: addrs[0].Town,
		        county: addrs[0].TraditionalCounty,
		        postcode: addrs[0].Postcode
		      } as AddressDto

		      this.$emit('address-selected', this.manualAddress)

		      this.hideAddressLookup = true
		    }
		  } catch (error) {
		    console.log(error)
		  } finally {
		    //
		  }
		}

		async setAddress (addressKey: string) {
		  await this.addressLookup(addressKey)
		}

		get postcodeRegexVal () {
		  return postcodeRegex
		}

		get numberRequired () {
		  return !!((this.manualAddress.flatNumber === undefined || this.manualAddress.flatNumber === '') &&
				(this.manualAddress.buildingNumber === undefined || this.manualAddress.buildingNumber === '') &&
				(this.manualAddress.buildingName === undefined || this.manualAddress.buildingName === ''))
		}
		// #endregion

		// #region Watches

		@Watch('preAddress')
		setManualAddress () {
		  if (this.preAddress) {
		    this.manualAddress = this.preAddress
		  }
		}

		@Watch('manualAddress.buildingName')
		@Watch('manualAddress.buildingNumber')
		@Watch('manualAddress.flatNumber')
		revalidate () {
		  setTimeout(() => {
		    (this.$refs.flatNo as unknown as CFInput).validate();
		    (this.$refs.houseNo as unknown as CFInput).validate();
		    (this.$refs.buildingName as unknown as CFInput).validate()
		  }, 1)
		}

  // #endregion
}
